import React, { useState, useEffect } from 'react';
import '../styles/Home.css';

const galleryContext = require.context('../assets/gallery-2025', false, /\.(jpe?g|png|gif|webp)$/i);
const galleryImages: string[] = galleryContext.keys().sort().map(galleryContext);

const RACE_DATE = new Date(2026, 6, 12, 9, 0, 0);

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  past: boolean;
}

function getTimeRemaining(target: Date): TimeRemaining {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, past: true };
  }
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    past: false,
  };
}

const Home: React.FC = () => {
  const [timeRemaining, setTimeRemaining] = useState(() => getTimeRemaining(RACE_DATE));
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(getTimeRemaining(RACE_DATE));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i !== null ? (i + 1) % galleryImages.length : null));
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i !== null ? (i - 1 + galleryImages.length) % galleryImages.length : null));
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxIndex]);

  return (
    <div className="home-container">
      <div className="hero-section">
        <h1>NORTH PARKLEY MARATHONS</h1>
        <p className="tagline">The race that tests your limits in the heart of North Park</p>
        <div className="countdown" aria-label="Countdown to race day">
          {timeRemaining.past ? (
            <p className="countdown-message">Race day is here!</p>
          ) : (
            <>
              <div className="countdown-item">
                <span className="countdown-value">{timeRemaining.days}</span>
                <span className="countdown-label">Days</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{timeRemaining.hours}</span>
                <span className="countdown-label">Hours</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{timeRemaining.minutes}</span>
                <span className="countdown-label">Minutes</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{timeRemaining.seconds}</span>
                <span className="countdown-label">Seconds</span>
              </div>
            </>
          )}
        </div>
      </div>
      
      <section className="race-info">
        <h2>ABOUT THE RACE</h2>
        <p>
          Inspired by the legendary Barkley Marathons, the North Parkley Marathons 
          challenges runners to navigate an unmarked course through the wilderness of North Park.
          Collect pages from hidden books, battle the elements, and test your physical and mental limits.
        </p>
        <p className="warning">
          This is not your typical trail race. Come prepared for a true challenge.
        </p>
      </section>
      
      <section className="event-details">
        <h2>EVENT DETAILS</h2>
        <div className="details-grid">
          <div className="detail-item">
            <h3>LOCATION</h3>
            <p>North Park<br />
            <span className="detail-note">Start/Finish and post-race cookout held at the RD's residence.</span><br />
            9990 Grubbs Road, Wexford PA 15090</p>
          </div>
          <div className="detail-item">
            <h3>DATE</h3>
            <p>Sunday, July 12, 2026<br />9:00 am</p>
          </div>
          <div className="detail-item">
            <h3>DISTANCE</h3>
            <p>Full – two loops, about a 50k<br />Fun Run – one loop, about a 25k<br />Baby Run – about a 10k</p>
          </div>
          <div className="detail-item">
            <h3>TIME LIMIT</h3>
            <p>4 hours per loop</p>
          </div>
        </div>
      </section>
      
      <section className="rules-section">
        <h2>BASIC RULES</h2>
        <ul>
          <li>Runners must find books hidden throughout the course</li>
          <li>Tear out the page corresponding to your bib number as proof</li>
          <li>Complete the designated route within the time limit</li>
          <li>More details to be revealed to registered participants</li>
        </ul>
      </section>

      <section className="gallery-section">
        <h2>2025 RACE PHOTOS</h2>
        <div className="gallery-grid">
          {galleryImages.map((src, index) => (
            <button
              key={src}
              type="button"
              className="gallery-item"
              onClick={() => setLightboxIndex(index)}
              aria-label={`View photo ${index + 1} of ${galleryImages.length}`}
            >
              <img src={src} alt={`North Parkley 2025 race scene ${index + 1}`} loading="lazy" />
            </button>
          ))}
        </div>
      </section>

      {lightboxIndex !== null && (
        <div
          className="gallery-lightbox"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={() => setLightboxIndex(null)}
            aria-label="Close photo viewer"
          >
            ×
          </button>
          <button
            type="button"
            className="lightbox-nav lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((i) => (i !== null ? (i - 1 + galleryImages.length) % galleryImages.length : null));
            }}
            aria-label="Previous photo"
          >
            ‹
          </button>
          <img
            src={galleryImages[lightboxIndex]}
            alt={`North Parkley 2025 race scene ${lightboxIndex + 1}`}
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="lightbox-nav lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((i) => (i !== null ? (i + 1) % galleryImages.length : null));
            }}
            aria-label="Next photo"
          >
            ›
          </button>
          <span className="lightbox-counter">
            {lightboxIndex + 1} / {galleryImages.length}
          </span>
        </div>
      )}
    </div>
  );
};

export default Home;
